import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { QueryRef } from 'apollo-angular';

import { ConfigValue, ConfigValueInput, ConnectSquareGQL, DisconnectSquareGQL, GetConfigValuesGQL, GetConfigValuesQuery, GetConfigValuesQueryVariables, GetSquareGQL, GetSquareQuery, SetConfigValuesGQL } from 'graphql.generated';
import { SubSink } from 'subsink';

import { PlusAuthenticationService } from '../core/public-api';
import { safeParseJSON } from '../js';
import { BrandingService } from '../services/branding.service';
import { FreyaNotificationsService } from '../services/freya-notifications.service';

import { MutateTransactionTypeComponent, ParsedTransactionType } from '../shared/mutate-transaction-type/mutate-transaction-type.component';

@Component({
  selector: 'app-payment-types',
  templateUrl: './payment-types.component.html',
  styleUrls: ['./payment-types.component.scss']
})
export class PaymentTypesComponent implements OnInit, OnDestroy {
  @ViewChild('mutateTransactionType') mutateTransactionTypeRef: MutateTransactionTypeComponent;

  subs = new SubSink();

  paymentTypeRows: ParsedTransactionType[];

  paymentTypesQueryRef: QueryRef<GetConfigValuesQuery, GetConfigValuesQueryVariables>;

  loading: boolean;

  square: GetSquareQuery['square'];

  // Used for discard changes functionality
  defaultValuesLookup: { [transactionTypeKey: string]: ParsedTransactionType } = {};

  constructor(
    public auth: PlusAuthenticationService,
    private branding: BrandingService,
    private getConfigGQL: GetConfigValuesGQL,
    private setConfigGQL: SetConfigValuesGQL,
    private notify: FreyaNotificationsService,
    private connectSquareGQL: ConnectSquareGQL,
    private disconnectSquareGQL: DisconnectSquareGQL,
    private getSquareGQL: GetSquareGQL,
  ) { }

  ngOnInit(): void {
    this.retrievePaymentTypes();
    this.checkSquareConnected();
    this.checkPaymentProviderAuthorization();
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  connectSquareClicked() {
    // TODO: generate stripe auth link and take you to it
    this.subs.sink = this.connectSquareGQL.mutate({}).subscribe((res) => {
      const url = res.data.connectSquare;
      if (url) {
        window.location.href = url;
      }

    });
  }

  disconnectSquareClicked() {
    // TODO: generate stripe auth link and take you to it
    this.subs.sink = this.disconnectSquareGQL.mutate({}).subscribe((res) => {
      const result = res.data.disconnectSquare;
      console.log(`[SQUARE]`, `disconnected`, result);
      this.checkSquareConnected();

    });
  }

  checkSquareConnected() {

    this.subs.sink = this.getSquareGQL.fetch({}, {
      fetchPolicy: 'network-only',
    }).subscribe((res) => {
      this.square = res.data.square;
      console.log(`[SQUARE]`, this.square);
    });
  }

  checkPaymentProviderAuthorization() {

    const url = new URL(location.href);
    const provider = url.searchParams.get('provider');
    const code = url.searchParams.get('code');
    const state = url.searchParams.get('state');
    // const zone = url.searchParams.get('zone');

    if (provider === 'square' && code) {
      this.authorizeSquare(code, state);
    }
  }

  authorizeSquare(code: string, state: string) {
    this.subs.sink = this.connectSquareGQL.mutate({
      code,
      state,
    }).subscribe((res) => {
      const connectSquare = res.data.connectSquare;
      console.log(`[SQUARE]`, connectSquare);

      // TODO: remove code and provider from url
      const url = new URL(location.href);
      url.searchParams.delete('code');
      url.searchParams.delete('provider');
      url.searchParams.delete('response_type');
      url.searchParams.delete('state');
      history.replaceState({}, '', url.toString());

      if (connectSquare === 'CONNECTED') {
        this.checkSquareConnected();
        this.retrievePaymentTypes();
      }
    });

  }

  retrievePaymentTypes() {
    if (this.paymentTypesQueryRef) {
      this.paymentTypesQueryRef.refetch();
      return;
    }

    this.paymentTypesQueryRef = this.getConfigGQL.watch({ keys: 'payment-type.*' }, { fetchPolicy: 'cache-and-network' });

    this.subs.sink = this.paymentTypesQueryRef.valueChanges
      .subscribe((res) => {

        this.loading = res.loading;

        if (this.loading) { return; }

        this.paymentTypeRows = res.data.getConfigValues
          .map((t) => this.parseTransactionType(t))
          .filter(Boolean);

      });
  }

  parseTransactionType(transactionType: ConfigValue): ParsedTransactionType {
    const parsedValue = safeParseJSON(transactionType.value);
    if (!parsedValue) { return; };
    return {
      key: transactionType.key,
      name: parsedValue.name,
      provider: parsedValue.provider,
      enabled: parsedValue.enabled,
      modified: false
    };
  }

  openCreateTransactionTypeDialog() {
    this.mutateTransactionTypeRef.mutateType = 'create';
    this.mutateTransactionTypeRef.openDialog();
  }

  saveTransactionType(transactionType: ParsedTransactionType) {
    const { name, provider, enabled } = transactionType;
    const configInput: ConfigValueInput = {
      key: transactionType.key,
      value: JSON.stringify({ name, provider, enabled })
    };

    this.subs.sink = this.setConfigGQL
      .mutate({ configs: [configInput] })
      .subscribe(() => {

        transactionType.modified = false;

        // Update default value;
        const defaultValue = this.defaultValuesLookup[transactionType.key];
        Object.assign(defaultValue, transactionType);

        this.notify.success('Changes saved.');
      });
  }

  markTransactionTypeAsModified(transactionType: ParsedTransactionType) {
    transactionType.modified = true;
  }

  discardChangesToTransactionType(transactionType: ParsedTransactionType) {
    if (!transactionType.modified) { return; };
    const defaultValue = this.defaultValuesLookup[transactionType.key];
    Object.assign(transactionType, defaultValue);
  }

}
